/**
 * Form for manually uploading a dance
 */

import { useState, useMemo } from 'react';
import { useNavigate, useBlocker } from 'react-router-dom';
import { useCreateDance, useDances } from '../hooks/useDances';
import { findDuplicates } from '../utils/duplicateDetector';
import type { CreateDanceRequest } from '../api/danceApi';

export function DanceUploadForm() {
  const navigate = useNavigate();
  const createDance = useCreateDance();

  const [formData, setFormData] = useState<CreateDanceRequest>({
    title: '',
    choreographer: '',
    formation: '',
    a1: '',
    a2: '',
    b1: '',
    b2: '',
    difficulty: undefined,
    style: undefined,
    notes: '',
    tags: [],
    callerNotes: '',
    teachingFigures: [],
    energyLevel: undefined,
    programRole: undefined,
    music: '',
    musicNotes: '',
    musicSource: '',
  });

  const { data: allDances } = useDances('ALL');
  const duplicates = useMemo(
    () => findDuplicates(formData.title, formData.formation, allDances || []),
    [formData.title, formData.formation, allDances]
  );

  const [tagsInput, setTagsInput] = useState<string>('');
  const [teachingInput, setTeachingInput] = useState<string>('');
  const [isDirty, setIsDirty] = useState(false);
  const blocker = useBlocker(isDirty && !createDance.isPending);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const dance = await createDance.mutateAsync(formData);
      setIsDirty(false);
      navigate(`/dances/${dance.danceId}`);
    } catch (error) {
      console.error('Failed to create dance:', error);
    }
  };

  const handleChange = (field: keyof CreateDanceRequest) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setIsDirty(true);
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleTagsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsDirty(true);
    const value = e.target.value;
    setTagsInput(value);
    const tags = value.split(',').map(tag => tag.trim()).filter(Boolean);
    setFormData((prev) => ({ ...prev, tags }));
  };

  const handleTeachingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsDirty(true);
    const value = e.target.value;
    setTeachingInput(value);
    const teachingFigures = value.split(',').map(f => f.trim()).filter(Boolean);
    setFormData((prev) => ({ ...prev, teachingFigures }));
  };

  const inputStyle = {
    width: '100%',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    fontSize: '1rem',
    border: '2px solid #e0e0e0',
    borderRadius: 'var(--radius-sm)',
    transition: 'border-color var(--transition-base)',
    fontFamily: 'inherit'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: 'var(--spacing-sm)',
    fontWeight: 'bold',
    color: 'var(--color-text)'
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: 'var(--spacing-xl)' }}>
      <form onSubmit={handleSubmit} className="card" style={{ backgroundColor: 'white', padding: 'var(--spacing-xl)' }}>
        <h2 style={{ marginTop: 0, marginBottom: 'var(--spacing-lg)' }}>Upload Dance Manually</h2>

        <div style={{
          padding: 'var(--spacing-md)',
          marginBottom: 'var(--spacing-xl)',
          backgroundColor: '#fff3cd',
          borderLeft: '4px solid var(--color-warning)',
          borderRadius: 'var(--radius-sm)',
          fontSize: '0.875rem'
        }}>
          <strong>Reminder:</strong> Please use gender-neutral terminology (Larks and Robins) instead of Ladies/Gents/Women/Men.
        </div>

        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <label htmlFor="title" style={labelStyle}>
            Title *
          </label>
          <input
            id="title"
            type="text"
            required
            value={formData.title}
            onChange={handleChange('title')}
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <label htmlFor="choreographer" style={labelStyle}>
            Choreographer
          </label>
          <input
            id="choreographer"
            type="text"
            value={formData.choreographer}
            onChange={handleChange('choreographer')}
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <label htmlFor="formation" style={labelStyle}>
            Formation *
          </label>
          <input
            id="formation"
            type="text"
            required
            placeholder="e.g., Improper contra"
            value={formData.formation}
            onChange={handleChange('formation')}
            style={inputStyle}
          />
        </div>

        {duplicates.length > 0 && (
          <div style={{
            marginBottom: 'var(--spacing-lg)',
            padding: 'var(--spacing-md)',
            backgroundColor: '#fff3cd',
            borderLeft: '4px solid var(--color-warning)',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.875rem'
          }}>
            <strong>Possible duplicate{duplicates.length > 1 ? 's' : ''} found:</strong>
            <ul style={{ margin: 'var(--spacing-sm) 0 0', paddingLeft: '1.2rem' }}>
              {duplicates.map(d => (
                <li key={d.dance.danceId}>
                  <a href={`/dances/${d.dance.danceId}`} target="_blank" rel="noopener noreferrer" style={{ color: '#856404' }}>
                    {d.dance.title}
                  </a>
                  {d.dance.choreographer && <span> by {d.dance.choreographer}</span>}
                </li>
              ))}
            </ul>
            <p style={{ margin: 'var(--spacing-sm) 0 0', color: '#856404' }}>
              You can still submit if this is a different dance.
            </p>
          </div>
        )}

        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <label htmlFor="a1" style={labelStyle}>
            A1 (16 beats) *
          </label>
          <textarea
            id="a1"
            required
            value={formData.a1}
            onChange={handleChange('a1')}
            rows={3}
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <label htmlFor="a2" style={labelStyle}>
            A2 (16 beats) *
          </label>
          <textarea
            id="a2"
            required
            value={formData.a2}
            onChange={handleChange('a2')}
            rows={3}
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <label htmlFor="b1" style={labelStyle}>
            B1 (16 beats) *
          </label>
          <textarea
            id="b1"
            required
            value={formData.b1}
            onChange={handleChange('b1')}
            rows={3}
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <label htmlFor="b2" style={labelStyle}>
            B2 (16 beats) *
          </label>
          <textarea
            id="b2"
            required
            value={formData.b2}
            onChange={handleChange('b2')}
            rows={3}
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-lg)' }}>
          <div>
            <label htmlFor="difficulty" style={labelStyle}>Difficulty</label>
            <select
              id="difficulty"
              value={formData.difficulty || ''}
              onChange={(e) => { setIsDirty(true); setFormData({ ...formData, difficulty: e.target.value as any || undefined }); }}
              style={inputStyle}
            >
              <option value="">Not specified</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          <div>
            <label htmlFor="style" style={labelStyle}>Style</label>
            <select
              id="style"
              value={formData.style || ''}
              onChange={(e) => { setIsDirty(true); setFormData({ ...formData, style: e.target.value as any || undefined }); }}
              style={inputStyle}
            >
              <option value="">Not specified</option>
              <option value="contra">Contra</option>
              <option value="square">Square</option>
              <option value="english-country">English Country</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label htmlFor="energyLevel" style={labelStyle}>Energy Level</label>
            <select
              id="energyLevel"
              value={formData.energyLevel || ''}
              onChange={(e) => { setIsDirty(true); setFormData({ ...formData, energyLevel: e.target.value as any || undefined }); }}
              style={inputStyle}
            >
              <option value="">Not specified</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div>
            <label htmlFor="programRole" style={labelStyle}>Program Role</label>
            <select
              id="programRole"
              value={formData.programRole || ''}
              onChange={(e) => { setIsDirty(true); setFormData({ ...formData, programRole: e.target.value as any || undefined }); }}
              style={inputStyle}
            >
              <option value="">Not specified</option>
              <option value="opener">Opener</option>
              <option value="filler">Filler</option>
              <option value="closer">Closer</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <label htmlFor="tags" style={labelStyle}>
            Tags
          </label>
          <input
            id="tags"
            type="text"
            value={tagsInput}
            onChange={handleTagsChange}
            placeholder="flowing, partner-heavy, beginner-friendly (comma-separated)"
            style={inputStyle}
          />
          {formData.tags && formData.tags.length > 0 && (
            <div style={{ marginTop: 'var(--spacing-sm)', display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-xs)' }}>
              {formData.tags.map(tag => (
                <span key={tag} className="tag-chip">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {formData.style === 'english-country' && (
          <div style={{
            marginBottom: 'var(--spacing-lg)',
            padding: 'var(--spacing-md)',
            backgroundColor: '#e8f4f8',
            borderRadius: 'var(--radius-sm)',
            borderLeft: '4px solid #17a2b8'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: 'var(--spacing-md)', color: '#17a2b8' }}>Music (English Country)</div>
            <div style={{ marginBottom: 'var(--spacing-md)' }}>
              <label htmlFor="music" style={labelStyle}>Tune Name</label>
              <input
                id="music"
                type="text"
                value={formData.music || ''}
                onChange={handleChange('music')}
                placeholder="e.g. Nottingham Lace"
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: 'var(--spacing-md)' }}>
              <label htmlFor="musicNotes" style={labelStyle}>Music Notes</label>
              <input
                id="musicNotes"
                type="text"
                value={formData.musicNotes || ''}
                onChange={handleChange('musicNotes')}
                placeholder="e.g. 32-bar jig, moderate tempo"
                style={inputStyle}
              />
            </div>
            <div>
              <label htmlFor="musicSource" style={labelStyle}>Music Source</label>
              <input
                id="musicSource"
                type="text"
                value={formData.musicSource || ''}
                onChange={handleChange('musicSource')}
                placeholder="e.g. RSCDS Book 12, session tune"
                style={inputStyle}
              />
            </div>
          </div>
        )}

        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <label htmlFor="notes" style={labelStyle}>Notes</label>
          <textarea
            id="notes"
            value={formData.notes}
            onChange={handleChange('notes')}
            rows={3}
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <label htmlFor="callerNotes" style={labelStyle}>Caller Notes</label>
          <textarea
            id="callerNotes"
            value={formData.callerNotes || ''}
            onChange={handleChange('callerNotes')}
            rows={3}
            placeholder="Teaching tips, walk-through suggestions, sequencing notes..."
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: 'var(--spacing-xl)' }}>
          <label htmlFor="teachingFigures" style={labelStyle}>Teaching Figures (comma-separated)</label>
          <input
            id="teachingFigures"
            type="text"
            value={teachingInput}
            onChange={handleTeachingChange}
            placeholder="balance & swing, robins chain"
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
          <button
            type="submit"
            disabled={createDance.isPending}
            className="btn btn-primary"
          >
            {createDance.isPending ? 'Creating...' : 'Create Dance'}
          </button>

          <button
            type="button"
            onClick={() => navigate('/')}
            className="btn btn-secondary"
          >
            Cancel
          </button>
        </div>

        {blocker.state === 'blocked' && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white', padding: 'var(--spacing-xl)',
              borderRadius: 'var(--radius-md)', maxWidth: '400px', width: '90%'
            }}>
              <h3 style={{ marginTop: 0 }}>Unsaved Changes</h3>
              <p>You have unsaved changes. Are you sure you want to leave?</p>
              <div style={{ display: 'flex', gap: 'var(--spacing-md)', justifyContent: 'flex-end' }}>
                <button onClick={() => blocker.reset?.()} className="btn btn-secondary">Stay</button>
                <button onClick={() => blocker.proceed?.()} className="btn btn-danger">Leave</button>
              </div>
            </div>
          </div>
        )}

        {createDance.isError && (
          <div style={{
            marginTop: 'var(--spacing-lg)',
            padding: 'var(--spacing-md)',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: 'var(--radius-sm)',
            borderLeft: '4px solid var(--color-danger)'
          }}>
            <strong>Error:</strong> {createDance.error.message}
          </div>
        )}
      </form>
    </div>
  );
}
